/**
 * Product Delivery Time Calculator
 * Calculates estimated delivery date based on current time and business rules
 *
 * Business Rules:
 * - Shipping: Only affected by Sundays (warehouse closed Sun only)
 * - Delivery/Transit: Continues during weekends (5 calendar days)
 */

class DeliveryCalculator {
  constructor(variantAvailabilityData = {}) {
    this.CUTOFF_HOUR = 20; // 8 PM cutoff time for shipping
    this.DELIVERY_DAYS = 4; // 4 calendar days for delivery (including weekends)
    this.currentVariantId = null;
    this.variantAvailabilityMap = variantAvailabilityData; // Pre-loaded variant availability data
    this.init();
  }

  init() {
    // Initial display update (combines stock check and content)
    this.updateDeliveryDisplay();

    // Update every minute (only if product is available)
    setInterval(() => {
      this.updateDeliveryInfo();
    }, 60000);

    // Add shake animation to Add to Cart button periodically (every 8-12 seconds)
    this.initShakeAnimation();

    // Listen for variant changes
    this.listenForVariantChanges();
  }

  /**
   * Check stock status and show/hide delivery box accordingly
   */
  checkStockStatus() {
    // Find the add to cart button to check availability
    const addToCartButton = document.querySelector('.single-add-to-cart-button');
    const deliveryBoxes = document.querySelectorAll('.delivery-time-box');
    const buyDeliveryContainers = document.querySelectorAll('.buyDelivery');

    if (!addToCartButton) return;

    const isAvailable = !addToCartButton.disabled && !addToCartButton.classList.contains('sold-out');

    deliveryBoxes.forEach(box => {
      if (isAvailable) {
        box.style.display = 'flex';
      } else {
        box.style.display = 'none';
      }
    });

    // Update buyDelivery background
    buyDeliveryContainers.forEach(container => {
      if (isAvailable) {
        // Check if dynamic checkout button is loaded
        this.checkDynamicCheckoutLoaded(container);
      } else {
        // Product not available, set background to var(--bg-body)
        container.style.backgroundColor = 'var(--bg-body)';
      }
    });

    return isAvailable;
  }

  /**
   * Listen for variant changes
   */
  listenForVariantChanges() {
    // Method 1: Listen for variant:changed custom event (modern Shopify themes)
    const productRoot = document.querySelector('[id^="MainProduct-"], [data-section-type*="product"], form[action*="/cart/add"]');

    if (productRoot) {
      productRoot.addEventListener('variant:changed', (event) => {
        if (event.detail && event.detail.variant && event.detail.variant.id) {
          this.currentVariantId = event.detail.variant.id;
          // Update immediately - no need to wait for DOM
          this.updateDeliveryDisplay();
        }
      });

      // Method 2: Listen for changes on variant input field
      productRoot.addEventListener('change', (event) => {
        const variantInput = event.target.form
          ? event.target.form.querySelector('[name="id"]')
          : productRoot.querySelector('[name="id"]');

        if (variantInput && variantInput.value) {
          this.currentVariantId = variantInput.value;
          // Update immediately - no need to wait for DOM
          this.updateDeliveryDisplay();
        }
      });
    } else {
      // Fallback: Listen on document body for variant changes
      document.body.addEventListener('change', (event) => {
        if (event.target &&
            event.target.name === 'id' &&
            event.target.form &&
            event.target.form.action.includes('/cart/add')) {
          this.currentVariantId = event.target.value;
          // Update immediately - no need to wait for DOM
          this.updateDeliveryDisplay();
        }
      });
    }
  }

  /**
   * Update delivery display - combines stock check and content update in one method
   * This is called immediately when variant changes (like promotional-discount does)
   */
  updateDeliveryDisplay() {
    const deliveryBoxes = document.querySelectorAll('.delivery-time-box');
    const buyDeliveryContainers = document.querySelectorAll('.buyDelivery');

    // Get availability from pre-loaded data - no DOM checking needed!
    let isAvailable = false;

    if (this.currentVariantId && this.variantAvailabilityMap[this.currentVariantId] !== undefined) {
      isAvailable = this.variantAvailabilityMap[this.currentVariantId];
    } else {
      // Fallback: check initial page load variant
      const variantInput = document.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        this.currentVariantId = variantInput.value;
        isAvailable = this.variantAvailabilityMap[this.currentVariantId] || false;
      }
    }

    // Update visibility and background immediately
    deliveryBoxes.forEach((box) => {
      box.style.display = isAvailable ? 'flex' : 'none';
    });

    buyDeliveryContainers.forEach((container) => {
      if (isAvailable) {
        // Product available - check if dynamic checkout button is loaded and set background
        this.checkDynamicCheckoutLoaded(container);
      } else {
        // Product not available, set background to var(--bg-body)
        container.style.backgroundColor = 'var(--bg-body)';
      }
    });

    // If available, update the content immediately
    if (isAvailable) {
      const now = new Date();
      const startDate = this.getStartDate(now);
      const deliveryDate = this.addCalendarDays(startDate, this.DELIVERY_DAYS);
      const timeUntilCutoff = this.getTimeUntilCutoff(now);

      const countdownText = this.formatCountdown(timeUntilCutoff);
      const dateText = this.formatDeliveryDate(deliveryDate);

      // Update countdown
      const countdownElements = document.querySelectorAll('.delivery-countdown');
      countdownElements.forEach(el => {
        el.textContent = countdownText;
      });

      // Update delivery date
      const dateElements = document.querySelectorAll('.delivery-date');
      dateElements.forEach(el => {
        el.textContent = dateText;
      });
    }
  }

  /**
   * Check if current time is Sunday or after Saturday 5 PM
   */
  isSundayOrAfterSaturday(date) {
    const day = date.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = date.getHours();

    // Sunday
    if (day === 0) {
      return true;
    }

    // Saturday after 5 PM
    if (day === 6 && hour >= this.CUTOFF_HOUR) {
      return true;
    }

    return false;
  }

  /**
   * Get next Monday from a given date (only from Sunday or Saturday after 5 PM)
   */
  getNextMonday(date) {
    const result = new Date(date);
    const day = result.getDay();

    // Calculate days until next Monday
    let daysUntilMonday;
    if (day === 0) { // Sunday
      daysUntilMonday = 1;
    } else { // Saturday after 5 PM
      daysUntilMonday = 2;
    }

    result.setDate(result.getDate() + daysUntilMonday);
    return result;
  }

  /**
   * Calculate the shipping date (when package leaves warehouse)
   * Warehouse closed on Sundays only
   */
  getStartDate(now) {
    // If Sunday or Saturday after 5 PM, ship on next Monday
    if (this.isSundayOrAfterSaturday(now)) {
      return this.getNextMonday(now);
    }

    // If after cutoff time on weekday (Mon-Sat), ship tomorrow
    if (now.getHours() >= this.CUTOFF_HOUR) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);

      // If tomorrow is Sunday, ship on Monday instead
      if (tomorrow.getDay() === 0) {
        return this.getNextMonday(tomorrow);
      }

      return tomorrow;
    }

    // If before cutoff time on weekday, ship today
    return new Date(now);
  }

  /**
   * Add calendar days to a date (INCLUDING weekends)
   * Transit and delivery continue during weekends
   */
  addCalendarDays(startDate, days) {
    const result = new Date(startDate);
    result.setDate(result.getDate() + days);
    return result;
  }

  /**
   * Calculate time remaining until cutoff
   */
  getTimeUntilCutoff(now) {
    const cutoffToday = new Date(now);
    cutoffToday.setHours(this.CUTOFF_HOUR, 0, 0, 0);

    // If it's Sunday or Saturday after cutoff, calculate until next Monday 5 PM
    if (this.isSundayOrAfterSaturday(now)) {
      const nextMonday = this.getNextMonday(now);
      nextMonday.setHours(this.CUTOFF_HOUR, 0, 0, 0);
      return nextMonday - now;
    }

    // If after cutoff today, calculate until tomorrow's cutoff
    if (now >= cutoffToday) {
      cutoffToday.setDate(cutoffToday.getDate() + 1);
    }

    return cutoffToday - now;
  }

  /**
   * Format countdown time
   */
  formatCountdown(milliseconds) {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));

    return `${hours} hours ${minutes} minutes`;
  }

  /**
   * Format delivery date
   */
  formatDeliveryDate(date) {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = days[date.getDay()];
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${dayName} ${day}/${month}/${year}`;
  }

  /**
   * Update delivery information on the page
   */
  updateDeliveryInfo() {
    // Check if product is available before updating
    const isAvailable = this.checkStockStatus();
    if (!isAvailable) return;

    const now = new Date();
    const startDate = this.getStartDate(now);
    const deliveryDate = this.addCalendarDays(startDate, this.DELIVERY_DAYS);
    const timeUntilCutoff = this.getTimeUntilCutoff(now);

    // Update countdown
    const countdownElements = document.querySelectorAll('.delivery-countdown');
    countdownElements.forEach(el => {
      el.textContent = this.formatCountdown(timeUntilCutoff);
    });

    // Update delivery date
    const dateElements = document.querySelectorAll('.delivery-date');
    dateElements.forEach(el => {
      el.textContent = this.formatDeliveryDate(deliveryDate);
    });
  }

  /**
   * Initialize shake animation for Add to Cart button
   */
  initShakeAnimation() {
    const addToCartButtons = document.querySelectorAll('.add-to-cart-shakeable');

    addToCartButtons.forEach(button => {
      if (!button.disabled && !button.classList.contains('sold-out')) {
        // Trigger shake animation every 8-12 seconds randomly
        const triggerShake = () => {
          button.classList.add('shake-active');

          setTimeout(() => {
            button.classList.remove('shake-active');
          }, 500);

          // Schedule next shake randomly between 8-12 seconds
          const nextShake = Math.random() * 4000 + 8000;
          setTimeout(triggerShake, nextShake);
        };

        // Start first shake after 3 seconds
        setTimeout(triggerShake, 3000);
      }
    });
  }

  /**
   * Check if dynamic checkout button is loaded and apply background to buyDelivery
   */
  checkDynamicCheckoutLoaded(container) {
    // If specific container provided, check only that one
    const containers = container ? [container] : document.querySelectorAll('.buyDelivery');

    containers.forEach(containerElement => {
      const checkButton = () => {
        // Check if shopify-payment-button exists and has loaded
        const paymentButton = containerElement.querySelector('.shopify-payment-button');
        const dynamicCheckoutButton = containerElement.querySelector('.shopify-payment-button__button');

        if (paymentButton && dynamicCheckoutButton) {
          // Dynamic checkout button is loaded, apply background
          containerElement.style.backgroundColor = '#E5F6FC';
          return true;
        }
        return false;
      };

      // Try to check immediately
      if (!checkButton()) {
        // If not loaded yet, set a temporary background first
        containerElement.style.backgroundColor = '#E5F6FC';

        // Then use MutationObserver to watch for changes
        const observer = new MutationObserver(() => {
          if (checkButton()) {
            observer.disconnect(); // Stop observing once button is found
          }
        });

        // Observe the container for child additions
        observer.observe(containerElement, {
          childList: true,
          subtree: true
        });

        // Also set a timeout fallback (in case button loads via different method)
        const checkInterval = setInterval(() => {
          if (checkButton()) {
            clearInterval(checkInterval);
            observer.disconnect();
          }
        }, 1000); // Check every 1000ms

        // Stop checking after 5 seconds to avoid infinite checking
        setTimeout(() => {
          clearInterval(checkInterval);
          observer.disconnect();
        }, 5000);
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Get variant availability data from window (set by Liquid)
    const variantData = window.productVariantAvailability || {};
    new DeliveryCalculator(variantData);
  });
} else {
  // Get variant availability data from window (set by Liquid)
  const variantData = window.productVariantAvailability || {};
  new DeliveryCalculator(variantData);
}
